import { useStartTypingMutation } from 'gcs-common/clients/api/entities/channels/channel-api';
import SendOrMarkReadButton from 'gcs-common/components/message-input/SendOrMarkReadButton';
import { CHAT_ROUTES } from 'gcs-common/constants/routes';
import { cn } from 'gcs-common/design-system/utils/tailwind-merge';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import {
  getChannelInputDisabled,
  getChannelInputIsSendingDisabled,
  getChannelInputPlaceholder,
  getChannelInputQuotedMessage,
  getChannelInputStagedFiles,
  getChannelInputStagedForwardMessageIds,
  getChannelInputText,
  getEtsMessageStaged,
} from 'gcs-common/slices/messageInput/messageInputSelector';
import { channelInputCleared } from 'gcs-common/slices/messageInput/messageInputSlice';
import {
  changeInputText,
  changeInputTextForEtsMessage,
} from 'gcs-common/slices/messageInput/messageInputThunks/changeInputText';
import { sendCurrentMessageState } from 'gcs-common/slices/messageInput/messageInputThunks/sendCurrentMessageState';
import throttle from 'lodash/throttle';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import FilePreviewer from '../FilePreviewer/FilePreviewer';
import ForwardChannelList from '../ForwardChannelList/ForwardChannelList';
import QuotedMessage from '../QuotedMessage/QuotedMessage';
import MessageInputField from './MessageInputField/MessageInputField';
import MessageInputFileButton from './MessageInputFileButton/MessageInputFileButton';
import MessageSuggestions from './MessageSuggestions/MessageSuggestions';
import SmileyMessageInput from './SmileyMessageInput/SmileyMessageInput';
import StageSelectedItem from './StageSelectedItem/StageSelectedItem';

interface MessageInputAreaProps {
  forwardingFromEts?: boolean;
}

const MessageInputArea = ({ forwardingFromEts = false }: MessageInputAreaProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedChannelId = useSelector(getSelectedChannelId);
  const files = useSelector(getChannelInputStagedFiles(selectedChannelId));
  const quotedMessageId = useSelector(getChannelInputQuotedMessage(selectedChannelId));
  const stagedForwardMessageIds = useSelector(getChannelInputStagedForwardMessageIds(selectedChannelId));
  const placeholder = useSelector(getChannelInputPlaceholder(selectedChannelId));
  const inputDisabled = useSelector(getChannelInputDisabled(selectedChannelId));
  const sendingDisabled = useSelector(getChannelInputIsSendingDisabled(selectedChannelId));
  const channelMessage = useSelector(getChannelInputText(selectedChannelId));
  const etsMessage = useSelector(getEtsMessageStaged);

  const inputText = forwardingFromEts ? etsMessage : channelMessage;

  const resetQuotedMessage = useCallback(() => {
    dispatch(channelInputCleared({ channelId: selectedChannelId }));
  }, [dispatch, selectedChannelId]);

  const startTypingMutation = useStartTypingMutation();

  const throttledStartTyping = useRef(
    throttle(
      (channelId: string) => {
        startTypingMutation.mutate({ params: { path: { channel_id: channelId } } });
      },
      5000,
      { leading: true, trailing: false },
    ),
  ).current;

  const handleSendMessage = useCallback(async () => {
    // @ts-ignore - sendCurrentMessageState actually needs this as parameter
    await dispatch(sendCurrentMessageState({ channelId: selectedChannelId }));
    throttledStartTyping.cancel(); // Reset typing after sending
    if (forwardingFromEts) {
      navigate(CHAT_ROUTES.CHANNEL(selectedChannelId));
    }
  }, [dispatch, selectedChannelId, navigate, forwardingFromEts, throttledStartTyping]);

  const handleTextChange = (text: string) => {
    if (forwardingFromEts) {
      dispatch(changeInputTextForEtsMessage({ inputText: text }));
    } else {
      dispatch(changeInputText({ channelId: selectedChannelId, inputText: text }));
      throttledStartTyping(selectedChannelId);
    }
  };

  const messageInputAreaClass = 'message-input-area';

  return (
    <>
      {stagedForwardMessageIds.length !== 0 && <ForwardChannelList />}
      {files.length !== 0 && <FilePreviewer multiple hoverBoundaryClass={messageInputAreaClass} />}
      <div
        className={cn(
          'z-10 flex flex-grow items-center overflow-x-hidden border-0 border-t border-t-neutral-6 border-solid bg-neutral-1 px-4 py-3',
          messageInputAreaClass,
        )}
      >
        <div className="flex-grow">
          {quotedMessageId && (
            <div className="p-2">
              <QuotedMessage onCancel={resetQuotedMessage} messageId={quotedMessageId} />
            </div>
          )}
          {stagedForwardMessageIds.length > 1 && (
            <div className="pl-3 text-neutral-9 text-sm">{stagedForwardMessageIds.length} Nachrichten ausgewählt</div>
          )}
          <StageSelectedItem />
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <MessageSuggestions />
            </div>
            <div className="flex items-center gap-2">
              {!forwardingFromEts && <MessageInputFileButton />}
              <div className="flex flex-grow items-center rounded-3xl border border-neutral-alpha-5 border-solid">
                {!forwardingFromEts && <SmileyMessageInput />}
                <MessageInputField
                  inputText={inputText}
                  placeholder={placeholder}
                  disabled={inputDisabled}
                  sendingDisabled={sendingDisabled}
                  onSendMessage={handleSendMessage}
                  onTextChange={handleTextChange}
                />
              </div>
              <SendOrMarkReadButton onSend={handleSendMessage} showMarkLabel />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageInputArea;
